import LandingPage from './components/Landing/LandingPage';
import MockTestPage from './components/Landing/MockTestPage';
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import './index.css';

import NewsSharePage from './components/News/NewsSharePage';
import LoginPage from './components/Auth/LoginPage';
import RegisterPage from './components/Auth/RegisterPage';
import Dashboard from './components/Dashboard/Dashboard';
import SubjectPicker from './components/Cards/SubjectPicker';
import StudySession from './components/Cards/StudySession';
import PrelimsPracticeSession from './components/Cards/PrelimsPracticeSession';
import MainsPracticeSession from './components/Cards/MainsPracticeSession';
import CardBrowser from './components/Cards/CardBrowser';
import SubjectView from './components/Cards/SubjectView';
import Bookmarks from './components/Cards/Bookmarks';
import ProfilePage from './components/Dashboard/ProfilePage';
import PYQPage from './components/PYQ/PYQPage';
import NewsPage from './components/News/NewsPage';
import AdminPanel from './components/Admin/AdminPanel';
import Layout from './components/Layout/Layout';
import PublicCardPage from './components/Cards/PublicCardPage';
import PublicPYQPage from './components/Cards/PublicPYQPage';
import AboutPage from './components/Landing/AboutPage';
import PrivacyPage from './components/Landing/PrivacyPage';
import ContactPage from './components/Landing/ContactPage';
import PYQSearchPage from './components/PYQ/PYQSearchPage';
import StudySyllabus from './components/StudySyllabus/StudySyllabus';
import PublicSubjectPage from './components/Cards/PublicSubjectPage';
import PageMeta from './components/SEO/PageMeta';
import { Helmet } from 'react-helmet-async';
import VisitorCounter from './components/VisitorCounter/VisitorCounter';

function RouteRobots() {
  const { pathname } = useLocation();
  const isPrivateRoute = /^\/(?:login|register|dashboard|prelims(?:\/|$)|mains(?:\/|$)|browse(?:\/|$)|subject(?:\/|$)|bookmarks(?:\/|$)|profile(?:\/|$)|admin(?:\/|$))/.test(pathname);

  if (!isPrivateRoute) return null;

  return (
    <Helmet>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
  );
}

function LegacyPYQRedirect() {
  const { id } = useParams();
  return <Navigate to={`/pyq-question/${encodeURIComponent(id)}`} replace />;
}

function NotFoundPage() {
  return (
    <main>
      <PageMeta
        title="Page not found | CivilFox"
        description="The CivilFox page you requested could not be found."
        robots="noindex, nofollow"
      />
      <h1>Page not found</h1>
      <p>The page may have moved or the link may be incorrect.</p>
      <Link to="/">Return to CivilFox</Link>
    </main>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <div className="spinner" style={{ width: 40, height: 40 }} />
      </div>
    );
  }

  return user ? children : <Navigate to="/login" replace />;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) return null;

  if (!user) return <Navigate to="/login" replace />;

  if (user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) return null;

  // Logged-in users hitting the landing or login page go to Prelims.
  return user ? <Navigate to="/dashboard" replace /> : children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RouteRobots />
        <Routes>
          {/* Public landing page */}
          <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />

          {/* Public news and content pages */}
          <Route path="/news" element={<NewsPage />} />
          <Route path="/news/:id" element={<NewsSharePage />} />
          <Route path="/card/:id" element={<PublicCardPage />} />
          <Route path="/pyq-question/:id" element={<PublicPYQPage />} />
          <Route path="/pyq/:id" element={<LegacyPYQRedirect />} />
          <Route path="/pyq" element={<PYQPage />} />
          <Route path="/topic/:subject" element={<PublicSubjectPage />} />

          {/* Public PYQ search and individual question pages */}
          <Route path="/pyq-search" element={<PYQSearchPage />} />
          <Route path="/mock-test" element={<MockTestPage />} />

          {/* Other public pages */}
          <Route path="/about" element={<AboutPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/contact" element={<ContactPage />} />

          <Route
            path="/study-upsc-syllabus"
            element={<StudySyllabus />}
          />
          <Route
            path="/study-upsc-syllabus/:subject"
            element={<StudySyllabus />}
          />
          <Route
            path="/study-upsc-syllabus/:subject/:chapter"
            element={<StudySyllabus />}
          />

          {/* Authentication */}
          <Route
            path="/login"
            element={<PublicRoute><LoginPage /></PublicRoute>}
          />
          <Route
            path="/register"
            element={<PublicRoute><RegisterPage /></PublicRoute>}
          />

          {/* Protected application */}
          <Route
            path="/"
            element={<ProtectedRoute><Layout /></ProtectedRoute>}
          >
            <Route path="dashboard" element={<Dashboard />} />
            {/* Prelims */}
            <Route
              path="prelims"
              element={<SubjectPicker examType="Prelims" />}
            />
            <Route
              path="prelims/:subject"
              element={<StudySession examType="Prelims" />}
            />
            {/* Prelims Practice by date */}
            <Route
              path="prelims/practice/:date"
              element={<PrelimsPracticeSession />}
            />

            {/* Mains */}
            <Route
              path="mains"
              element={<SubjectPicker examType="Mains" />}
            />
            <Route
              path="mains/practice/:date"
              element={<MainsPracticeSession />}
            />
            <Route
              path="mains/:subject"
              element={<StudySession examType="Mains" />}
            />

            {/* Other protected pages */}
            <Route path="browse" element={<CardBrowser />} />
            <Route path="subject/:subjectName" element={<SubjectView />} />
            <Route path="bookmarks" element={<Bookmarks />} />
            <Route path="profile" element={<ProfilePage />} />

            {/* Admin */}
            <Route
              path="admin"
              element={
                <AdminRoute>
                  <AdminPanel />
                </AdminRoute>
              }
            />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <VisitorCounter />
      </BrowserRouter>
    </AuthProvider>
  );
}